import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { GraduationCap, Users, BookOpen, CheckCircle } from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0E366A] via-[#1a4d7a] to-[#0E366A]">
      {/* Header */}
      <header className="bg-[#F7B538] border-b border-[#e5a627] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img 
              src="/lis_logo.png" 
              alt="London Islamic School" 
              className="h-16"
            />
            <div>
              <h2 className="text-[#0E366A] font-serif text-xl font-bold">London Islamic School</h2>
              <p className="text-[#0E366A] text-xs italic">Learning today. Leading tomorrow. Inspired Forever.</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Button 
              data-testid="parent-login-btn"
              variant="ghost" 
              onClick={() => navigate('/login')}
              className="text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              Parent Login
            </Button>
            <Button 
              data-testid="admin-login-btn"
              variant="ghost" 
              onClick={() => navigate('/admin')}
              className="text-[#0E366A] hover:bg-[#0E366A] hover:text-white transition-colors"
            >
              Admin Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 text-center">
        <div className="max-w-4xl mx-auto space-y-6">
          <h1 
            data-testid="hero-title"
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight"
          >
            Student Enrollment
            <span className="block text-[#F7B538] mt-2">Waiting List</span>
          </h1>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto">
            Welcome to London Islamic School's enrollment system. Join our waiting list for Pre-School to G8 and secure your child's place in our caring educational community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
            <Button 
              data-testid="apply-now-btn"
              size="lg" 
              onClick={() => navigate('/register')}
              className="bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              Apply Now
            </Button>
            <Button 
              data-testid="existing-applicant-btn"
              size="lg" 
              variant="outline"
              onClick={() => navigate('/login')}
              className="border-2 border-white text-white hover:bg-white hover:text-[#0E366A] px-8 py-6 text-lg rounded-full transition-all font-semibold"
            >
              Existing Applicant
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-[#F7B538] rounded-full flex items-center justify-center mb-4">
              <GraduationCap className="w-7 h-7 text-[#0E366A]" />
            </div>
            <h3 className="text-xl font-semibold text-[#0E366A] mb-3">Pre-School to G8</h3>
            <p className="text-gray-600">Comprehensive education program from Pre-School through Grade 8, fostering academic and spiritual growth.</p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-[#F7B538] rounded-full flex items-center justify-center mb-4">
              <Users className="w-7 h-7 text-[#0E366A]" />
            </div>
            <h3 className="text-xl font-semibold text-[#0E366A] mb-3">Fair & Transparent</h3>
            <p className="text-gray-600">Our waitlist system ensures fair enrollment with real-time position tracking and automated notifications.</p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="w-14 h-14 bg-[#F7B538] rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-7 h-7 text-[#0E366A]" />
            </div>
            <h3 className="text-xl font-semibold text-[#0E366A] mb-3">Easy Application</h3>
            <p className="text-gray-600">Simple online application process with instant confirmation and application code for tracking.</p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[#0E366A] mb-12">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#F7B538] rounded-full flex items-center justify-center text-[#0E366A] font-bold text-xl mx-auto mb-4">1</div>
              <h4 className="font-semibold text-[#0E366A] mb-2">Register</h4>
              <p className="text-sm text-gray-600">Create your parent account</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#F7B538] rounded-full flex items-center justify-center text-[#0E366A] font-bold text-xl mx-auto mb-4">2</div>
              <h4 className="font-semibold text-[#0E366A] mb-2">Apply</h4>
              <p className="text-sm text-gray-600">Submit student information</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#F7B538] rounded-full flex items-center justify-center text-[#0E366A] font-bold text-xl mx-auto mb-4">3</div>
              <h4 className="font-semibold text-[#0E366A] mb-2">Track</h4>
              <p className="text-sm text-gray-600">Monitor waitlist position</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-[#F7B538] rounded-full flex items-center justify-center text-[#0E366A] font-bold text-xl mx-auto mb-4">4</div>
              <h4 className="font-semibold text-[#0E366A] mb-2">Enroll</h4>
              <p className="text-sm text-gray-600">Receive interview invitation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400 mb-4">London Islamic School - Excellence in Education</p>
          <p className="text-sm text-gray-500">© 2025 London Islamic School. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;